import { useEffect, useState } from 'react';
import {
  getCompanyCouriersDDL,
  getLocationDDL,
  getProductDDL,
  getTeamsDDLOFFeature,
} from '../../../common/utils/dropDown';
import { GetCompanySensors, SensorData } from '../../../apis/settings';
import { CourierData, TeamDDLOption } from '@/apis/lookup';
import { ProductData } from '@/apis/onboarding';
import { LocationData } from '@/apis/types';

interface ApiResponse {
  data: unknown[];
  [key: string]: unknown;
}

/** Hide Default from pickers when at least one other team exists (matches single-shipment DDL behavior). */
export function teamsOptionsForBulkUpload(
  items: TeamDDLOption[]
): TeamDDLOption[] {
  if (!items.length) return [];
  const hasDefault = items.some(
    (t) => (t.name || '').toLowerCase() === 'default'
  );
  const nonDefault = items.filter(
    (t) => (t.name || '').toLowerCase() !== 'default'
  );
  if (hasDefault && nonDefault.length >= 1) {
    return nonDefault;
  }
  return items;
}

interface UseBulkUploadDataReturn {
  data: unknown[];
  setData: (data: unknown[]) => void;
  couriersList: CourierData[];
  sensorsList: SensorData[];
  locationList: LocationData[];
  productList: ProductData[];
  /** Teams from API (unfiltered); use for company team-count semantics. */
  allTeamsUnfiltered: TeamDDLOption[];
  /** Teams shown in bulk pickers (Default omitted when other teams exist). */
  allTeams: TeamDDLOption[];
}

/**
 * Custom hook for managing bulk upload data and fetching dropdown options
 *
 * @param token - Authentication token for API calls
 * @param apiResponse - Initial API response containing data array
 * @returns Object containing data state and dropdown lists
 */
export const useBulkUploadData = (
  token: string,
  apiResponse: ApiResponse
): UseBulkUploadDataReturn => {
  const [data, setData] = useState<unknown[]>(apiResponse.data);
  const [couriersList, setCouriersList] = useState<CourierData[]>([]);
  const [sensorsList, setSensorsList] = useState<SensorData[]>([]);
  const [locationList, setLocationList] = useState<LocationData[]>([]);
  const [productList, setProductList] = useState<ProductData[]>([]);
  const [allTeamsUnfiltered, setAllTeamsUnfiltered] = useState<TeamDDLOption[]>(
    []
  );
  const [allTeams, setAllTeams] = useState<TeamDDLOption[]>([]);

  useEffect(() => {
    const fetchDropdowns = async (): Promise<void> => {
      const [teams, couriers, sensorsRes, products, locations] =
        await Promise.all([
          getTeamsDDLOFFeature(token, 'BulkShipment'),
          getCompanyCouriersDDL(token),
          GetCompanySensors(token),
          getProductDDL(token),
          getLocationDDL(token),
        ]);

      const rawTeams = Array.isArray(teams) ? teams : [];
      setAllTeamsUnfiltered(rawTeams);
      setAllTeams(teamsOptionsForBulkUpload(rawTeams));

      if (couriers?.length) setCouriersList(couriers);
      if (products?.length) setProductList(products);
      if (locations?.length) setLocationList(locations);

      const sensorsData = sensorsRes?.data?.data;
      if (sensorsData?.is_success) setSensorsList(sensorsData?.data);
    };

    fetchDropdowns();
  }, [token]);

  return {
    data,
    setData,
    couriersList,
    sensorsList,
    locationList,
    productList,
    allTeamsUnfiltered,
    allTeams,
  };
};
